type FilePresentationInput = Readonly<{
  name?: unknown;
  type?: unknown;
  path?: unknown;
}>;

export type FileKind =
  | 'image'
  | 'pdf'
  | 'text'
  | 'presentation'
  | 'spreadsheet'
  | 'document'
  | 'audio'
  | 'video'
  | 'archive'
  | 'link'
  | 'file';

const normalizeString = (value: unknown): string => (typeof value === 'string' ? value.trim().toLowerCase() : '');

const getPathString = (path: unknown): string => {
  if (typeof path === 'string') return path;
  if (path && typeof path === 'object' && 'name' in path) {
    return normalizeString((path as { name?: unknown }).name);
  }
  return '';
};

const getExtension = (file: FilePresentationInput): string => {
  const source = `${normalizeString(file.name)} ${getPathString(file.path)}`;
  const match = source.match(/\.([a-z0-9]{1,8})(?:[?#\s]|$)/i);
  return match?.[1]?.toLowerCase() ?? '';
};

const getMimeType = (file: FilePresentationInput): string => {
  const directType = normalizeString(file.type);
  if (directType.includes('/')) return directType;

  if (file.path && typeof file.path === 'object' && 'type' in file.path) {
    return normalizeString((file.path as { type?: unknown }).type);
  }

  return directType;
};

const FORMAT_BY_MIME: ReadonlyArray<readonly [string, string]> = [
  ['application/pdf', 'PDF'],
  ['wordprocessingml', 'DOCX'],
  ['application/msword', 'DOC'],
  ['spreadsheetml', 'XLSX'],
  ['application/vnd.ms-excel', 'XLS'],
  ['presentationml', 'PPTX'],
  ['application/vnd.ms-powerpoint', 'PPT'],
  ['image/jpeg', 'JPG'],
  ['image/png', 'PNG'],
  ['image/webp', 'WEBP'],
  ['image/gif', 'GIF'],
  ['image/svg', 'SVG'],
  ['text/plain', 'TXT'],
  ['text/csv', 'CSV'],
  ['application/json', 'JSON'],
  ['video/mp4', 'MP4'],
  ['audio/mpeg', 'MP3'],
  ['application/zip', 'ZIP'],
];

export const getFileFormatLabel = (file: FilePresentationInput): string => {
  const mime = getMimeType(file);
  const extension = getExtension(file) || (!mime.includes('/') && mime.length <= 8 ? mime : '');
  if (extension) return extension.slice(0, 5).toUpperCase();

  const mapped = FORMAT_BY_MIME.find(([pattern]) => mime.includes(pattern))?.[1];
  if (mapped) return mapped;

  const subtype = mime.split('/')[1]?.split(/[.+-]/)[0] ?? '';
  if (subtype && subtype.length <= 5) return subtype.toUpperCase();

  return 'FILE';
};

export const getFileKind = (file: FilePresentationInput & { data?: unknown }): FileKind => {
  const data = file.data && typeof file.data === 'object' ? (file.data as { isLink?: unknown }) : null;
  if (data?.isLink) return 'link';

  const mime = getMimeType(file);
  const extension = getExtension(file) || (!mime.includes('/') && mime.length <= 8 ? mime : '');

  if (
    mime.startsWith('image/') ||
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tif', 'tiff', 'heic'].includes(extension)
  ) {
    return 'image';
  }
  if (mime === 'application/pdf' || extension === 'pdf') return 'pdf';
  if (
    mime.includes('presentation') ||
    mime.includes('powerpoint') ||
    ['ppt', 'pptx', 'odp', 'key'].includes(extension)
  ) {
    return 'presentation';
  }
  if (
    mime.includes('spreadsheet') ||
    mime.includes('excel') ||
    ['xls', 'xlsx', 'ods', 'numbers', 'csv'].includes(extension)
  ) {
    return 'spreadsheet';
  }
  if (mime.includes('word') || ['doc', 'docx', 'odt', 'pages', 'rtf'].includes(extension)) return 'document';
  if (
    mime.startsWith('text/') ||
    ['txt', 'md', 'json', 'xml', 'html', 'css', 'js', 'ts'].includes(extension)
  ) {
    return 'text';
  }
  if (mime.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'].includes(extension)) return 'audio';
  if (mime.startsWith('video/') || ['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v'].includes(extension)) return 'video';
  if (
    mime.includes('zip') ||
    mime.includes('compressed') ||
    ['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)
  ) {
    return 'archive';
  }

  return 'file';
};
